/**
 * CRD Metadata Module
 * 
 * Handles reading and writing the hidden CRD-META comment block
 * that tracks document version and section completion status.
 */

const CRD_META_REGEX = /<!--\s*CRD-META\s*\n([\s\S]*?)-->/;

/**
 * Get the default metadata object for new CRDs
 * @returns {Object} Default metadata with all sections set to not_started
 */
export function getDefaultMeta() {
  return {
    version: "0.1.0",
    sections: {
      requirements: "not_started",
      design_specs: "not_started",
      a11y_approval: "not_started",
      cms: "not_started",
      dev_guidance: "not_started",
      docs: "not_started",
      checklist: "not_started"
    }
  };
}

/**
 * Parse CRD metadata from markdown content
 * @param {string} markdownContent - The markdown content to parse
 * @returns {Object} Parsed metadata object or default if not found
 */
export function parseCrdMeta(markdownContent) {
  console.log('🔍 parseCrdMeta called, content length:', markdownContent?.length);
  
  const match = markdownContent.match(CRD_META_REGEX);
  
  if (!match) {
    console.log('🔍 parseCrdMeta returning: default meta (no match found)');
    return getDefaultMeta();
  }
  
  try {
    const metaContent = match[1];
    const meta = { sections: {} };
    
    // Parse version
    const versionMatch = metaContent.match(/version:\s*([^\n]+)/);
    meta.version = versionMatch ? versionMatch[1].trim() : "0.1.0";
    
    // Parse sections
    const sectionsMatch = metaContent.match(/sections:\s*\n([\s\S]+)/);
    if (sectionsMatch) {
      const sectionLines = sectionsMatch[1].split('\n');
      for (const line of sectionLines) {
        const sectionMatch = line.match(/^\s+([^:]+):\s*(.+)/);
        if (sectionMatch) {
          const key = sectionMatch[1].trim();
          const value = sectionMatch[2].trim();
          meta.sections[key] = value;
        }
      }
    }
    
    // Ensure all sections exist with defaults
    const defaultMeta = getDefaultMeta();
    for (const key in defaultMeta.sections) {
      if (!meta.sections[key]) {
        meta.sections[key] = defaultMeta.sections[key];
      }
    }
    
    console.log('🔍 parseCrdMeta returning:', meta);
    return meta;
  } catch (error) {
    console.error('Error parsing CRD metadata:', error);
    console.log('🔍 parseCrdMeta returning: default meta (error occurred)');
    return getDefaultMeta();
  }
}

/**
 * Update or create CRD metadata block in markdown content
 * @param {string} markdownContent - The markdown content to update
 * @param {Object} newMeta - The new metadata object
 * @returns {string} Updated markdown content with metadata block
 */
export function updateCrdMeta(markdownContent, newMeta) {
  // Generate the metadata block
  const metaBlock = generateMetaBlock(newMeta);
  
  // Check if metadata block already exists
  if (CRD_META_REGEX.test(markdownContent)) {
    // Replace existing block
    return markdownContent.replace(CRD_META_REGEX, metaBlock);
  } else {
    // Prepend new block to content
    return metaBlock + '\n\n' + markdownContent;
  }
}

/**
 * Generate the metadata comment block string
 * @param {Object} meta - The metadata object
 * @returns {string} Formatted metadata comment block
 */
function generateMetaBlock(meta) {
  const lines = [
    '<!-- CRD-META',
    `version: ${meta.version || '0.1.0'}`,
    'sections:'
  ];
  
  // Add sections in consistent order (must match template order)
  const sectionOrder = ['requirements', 'design_specs', 'a11y_approval', 'cms', 'dev_guidance', 'docs', 'checklist'];
  for (const key of sectionOrder) {
    const status = meta.sections[key] || 'not_started';
    lines.push(`  ${key}: ${status}`);
  }
  
  lines.push('-->');
  
  return lines.join('\n');
}

/**
 * Update a specific section's status
 * @param {Object} meta - The current metadata object
 * @param {string} sectionKey - The section key to update
 * @param {string} status - The new status (not_started, in_progress, complete)
 * @returns {Object} Updated metadata object
 */
export function updateSectionStatus(meta, sectionKey, status) {
  const validStatuses = ['not_started', 'in_progress', 'complete'];
  
  if (!validStatuses.includes(status)) {
    console.warn(`Invalid status: ${status}. Using 'not_started'`);
    status = 'not_started';
  }
  
  return {
    ...meta,
    sections: {
      ...meta.sections,
      [sectionKey]: status
    }
  };
}

/**
 * Increment the version number
 * @param {string} version - Current version string (e.g., "0.1.0")
 * @param {string} type - Type of increment: 'major', 'minor', or 'patch'
 * @returns {string} New version string
 */
export function incrementVersion(version, type = 'patch') {
  const parts = version.split('.').map(Number);
  
  switch (type) {
    case 'major':
      parts[0]++;
      parts[1] = 0;
      parts[2] = 0;
      break;
    case 'minor':
      parts[1]++;
      parts[2] = 0;
      break;
    case 'patch':
    default:
      parts[2]++;
      break;
  }
  
  return parts.join('.');
}
